import { useMoveInventoryCategories, useMoveInventoryOptions } from '@/composables';
import { type InventoryCategory } from '@/types/InventoryCategory';
import type { InventoryOption } from '@/types/InventoryOption';
import { type MoveInventoryGroupData, type MoveInventoryItemData } from '@/types/MoveInventoryGroupData';
import { defineStore } from 'pinia';
import { useSessionStore } from '@/store';

export const useInventoryControllerStore = defineStore('inventoryController', () => {
    const { inventoryCategories } = useMoveInventoryCategories();
    const { inventoryOptions } = useMoveInventoryOptions();
    const sessionStore = useSessionStore();

    const CUSTOM_CATEGORY_ID = computed(
        () => inventoryCategories.value?.find((category) => category.name === 'Custom')?.id,
    );
    const CARTON_CATEGORY_ID = computed(
        () => inventoryCategories.value?.find((category) => category.slug === 'cartons_boxes')?.id,
    );

    const model = ref<MoveInventoryGroupData[]>([]);
    const isInitialised = ref(false);

    const activeRoomIndex = ref<number | null>(null);

    // Add save loading state
    const saveLoading = ref(false);

    const currentCategory = computed(() => {
        if (activeRoomIndex.value !== null && Number.isInteger(activeRoomIndex.value)) {
            const roomCategoryId = model.value[activeRoomIndex.value].inventory_category_id;

            return inventoryCategories.value?.find((category) => category.id === roomCategoryId);
        }

        return null;
    });

    const activeRoomItemCount = computed(() => {
        if (activeRoomIndex.value === null) {
            return model.value.reduce((acc, room) => acc + room.items.reduce((acc, item) => acc + item.qty, 0), 0);
        }

        if (Number.isInteger(activeRoomIndex.value)) {
            let count = 0;
            model.value[activeRoomIndex.value].items.forEach((item: MoveInventoryItemData) => {
                count += item.qty;
            });
            return count;
        }

        return 0;
    });

    const activeRoomIsCustom = computed(() => {
        if (activeRoomIndex.value === null) {
            return false;
        }

        return model.value[activeRoomIndex.value].inventory_category_slug === 'custom';
    });

    const activeRoomName = computed(() => {
        if (activeRoomIndex.value === null) {
            // TODO: change this back to 'All items' when we have a final decision
            return 'Item summary';
        }
        return model.value[activeRoomIndex.value].room_name;
    });

    function setActiveRoomIndex(index: number | null) {
        activeRoomIndex.value = index;
    }

    const CATEGORY_ICON_MAP: Record<string, string> = {
        custom: 'system-miscellaneous',
        bedroom: 'system-bed',
        lounge: 'system-chair',
        kitchen: 'system-kitchen',
        dining: 'system-dining',
        laundry: 'system-laundry',
        office: 'system-laptop',
        hall: 'system-picture',
        garage: 'system-shed',
        outside: 'system-bike',
        shed: 'system-shed2',
        outdoor_area: 'system-outdoor-area',
        rumpus: 'system-rumpus',
        cartons_boxes: 'system-box',
        clothing: 'system-clothing',
        other: 'system-office',
    };

    function getIconByCategorySlug(categoryName: string = ''): string {
        // Handle numbered category slugs (e.g., "bedroom_1" -> "bedroom")
        const baseCategoryName = categoryName.replace(/_\d+$/, '');

        // Try the exact slug first, then the base slug. Tenants can add their own
        // categories, which this map cannot know about, so fall back to the custom-room
        // icon rather than rendering none at all.
        return CATEGORY_ICON_MAP[categoryName] || CATEGORY_ICON_MAP[baseCategoryName] || 'system-miscellaneous';
    }

    // Debounced auto-save function for inventory
    let autoSaveTimeout: ReturnType<typeof setTimeout> | null = null;
    let lastSavedData: string | null = null;
    let pendingSave = false;

    // Async save routine that handles the actual API call
    const performSave = async () => {
        try {
            // Prepare the inventory data in the grouped format expected by the Laravel backend
            const inventoryData = model.value
                .filter((room) => room.items.some((i) => i.qty > 0))
                .map((room) => ({
                    inventory_category_id: room.inventory_category_id,
                    room_name: room.room_name,
                    items: room.items.filter((item) => item.qty > 0), // Only include items with quantity > 0
                }));

            // Check if data has actually changed since last save
            const currentDataHash = JSON.stringify(inventoryData);
            if (lastSavedData === currentDataHash) {
                return; // No changes, skip save
            }

            saveLoading.value = true;

            // Get the move ID and order ID from the session store
            const moveId = sessionStore.getMoveId;
            const orderId = sessionStore.getOrderId;

            if (!moveId) {
                console.error('Move ID not available from session store');
                return;
            }

            if (!orderId) {
                console.error('Order ID not available from session store');
                return;
            }

            // Use the updateCurrent endpoint for auto-save to update existing records instead of creating new versions
            // This endpoint should use UpdateCurrentMoveInventory action like the tenant API
            await window.axios.patch(`/customers/book/orders/${orderId}/inventory/current`, {
                move: {
                    inventory: inventoryData,
                },
            });

            // Store the hash of successfully saved data
            lastSavedData = currentDataHash;
        } catch (error) {
            console.error('Failed to auto-save inventory:', error);
        } finally {
            saveLoading.value = false;

            // If there was a pending save request during this save, schedule a trailing run
            if (pendingSave) {
                pendingSave = false;
                autoSaveTimeout = setTimeout(() => {
                    performSave();
                }, 500);
            }
        }
    };

    const autoSaveInventory = () => {
        // Clear any existing timeout
        if (autoSaveTimeout) {
            clearTimeout(autoSaveTimeout);
        }

        // If a save is currently in progress, mark that we have a pending save
        if (saveLoading.value) {
            pendingSave = true;
            return;
        }

        // Set a new timeout to debounce the save
        autoSaveTimeout = setTimeout(() => {
            performSave();
        }, 500);
    };

    // Force immediate save, cancelling any pending timeouts
    const flushAutoSave = async () => {
        // Cancel any pending timeout
        if (autoSaveTimeout) {
            clearTimeout(autoSaveTimeout);
            autoSaveTimeout = null;
        }

        // Clear pending save flag since we're doing immediate save
        pendingSave = false;

        // Perform immediate save
        await performSave();
    };

    function handleDecrease(inventoryOptionId: number) {
        if (activeRoomIndex.value === null) {
            throw new Error('handleDecrease: No active room selected');
        }

        const roomOptionIndex = model.value[activeRoomIndex.value].items.findIndex(
            (item) => item.inventory_option_id === inventoryOptionId,
        );

        if (roomOptionIndex === -1) {
            throw new Error('handleDecrease: Option not found in room');
        }

        model.value[activeRoomIndex.value].items[roomOptionIndex].qty--;

        if (model.value[activeRoomIndex.value].items[roomOptionIndex].qty === 0) {
            model.value[activeRoomIndex.value].items.splice(roomOptionIndex, 1);
        }

        // Auto-save after decreasing quantity
        autoSaveInventory();
    }

    function handleIncrease(inventoryOptionId: number) {
        // console.log(inventoryOptionId);

        if (activeRoomIndex.value === null) {
            throw new Error('handleIncrease: No active room selected');
        }

        const roomOptionIndex = model.value[activeRoomIndex.value].items.findIndex(
            (item) => item.inventory_option_id === inventoryOptionId,
        );

        if (roomOptionIndex === -1) {
            model.value[activeRoomIndex.value].items.push({
                inventory_option_id: inventoryOptionId,
                qty: 1,
            });
        } else {
            model.value[activeRoomIndex.value].items[roomOptionIndex].qty++;
        }

        // console.log({
        //     activeRoomIndex: activeRoomIndex.value,
        //     roomOptionIndex,
        //     room: model.value[activeRoomIndex.value],
        // });

        // Auto-save after increasing quantity
        autoSaveInventory();
    }

    function handleSetQuantity(inventoryOptionId: number, quantity: number) {
        if (activeRoomIndex.value === null) {
            throw new Error('handleSetQuantity: No active room selected');
        }

        const roomOptionIndex = model.value[activeRoomIndex.value].items.findIndex(
            (item) => item.inventory_option_id === inventoryOptionId,
        );

        if (roomOptionIndex === -1) {
            throw new Error('handleSetQuantity: Option not found in room');
        }

        model.value[activeRoomIndex.value].items[roomOptionIndex].qty = quantity;

        // Auto-save after setting quantity
        autoSaveInventory();
    }

    function addRoom(room: MoveInventoryGroupData) {
        const newRoom = JSON.parse(JSON.stringify(room));
        newRoom.items = [];
        const lastRoomIndex = model.value.findLastIndex((r) => r.inventory_category_id === room.inventory_category_id);
        model.value.splice(lastRoomIndex + 1, 0, newRoom);

        setActiveRoomIndex(lastRoomIndex + 1);

        // number the rooms of this category type
        const categoryRooms = model.value.filter((cRoom) => cRoom.inventory_category_id === room.inventory_category_id);
        categoryRooms.forEach((cRoom, index) => {
            cRoom.room_name = `${cRoom.inventory_category_name} ${index + 1}`;
        });
    }

    function addCustomRoom(roomName: string) {
        if (!CUSTOM_CATEGORY_ID.value) {
            throw new Error('addCustomRoom: Custom category ID not found');
        }

        model.value.unshift({
            inventory_category_id: CUSTOM_CATEGORY_ID.value,
            inventory_category_name: 'Custom',
            inventory_category_slug: 'custom',
            room_name: roomName,
            items: [],
        });

        setActiveRoomIndex(0);
    }

    function removeRoom(index: number) {
        const activeRoomIsBeingDeleted = activeRoomIndex.value === index;

        if (activeRoomIsBeingDeleted) {
            setActiveRoomIndex(Math.max(index - 1, 0));
        } else if (activeRoomIndex.value !== null && activeRoomIndex.value > index) {
            setActiveRoomIndex(Math.max(activeRoomIndex.value - 1, 0));
        }

        if (model.value[index].inventory_category_slug === 'custom') {
            model.value.splice(index, 1);
            return;
        }

        model.value.splice(index, 1);

        // number the rooms of this category type
        const categoryRooms = model.value.filter(
            (cRoom) => cRoom.inventory_category_id === model.value[index - 1].inventory_category_id,
        );
        if (categoryRooms.length > 1) {
            categoryRooms.forEach((cRoom, index) => {
                cRoom.room_name = `${cRoom.inventory_category_name} ${index + 1}`;
            });
        } else {
            categoryRooms[0].room_name = categoryRooms[0].inventory_category_name;
        }
    }

    function isFirstRoomOfCategory(room: MoveInventoryGroupData) {
        // if the room is the first room of its category return true, otherwise return false
        const categoryRooms = model.value.filter((r) => r.inventory_category_id === room.inventory_category_id);
        return categoryRooms.indexOf(room) === 0;
    }

    const getTotalItems = computed(() => {
        return model.value.reduce((acc, room) => acc + room.items.reduce((acc, item) => acc + item.qty, 0), 0);
    });

    const cartonCategoryInventoryOptions = computed(
        () =>
            inventoryOptions.value
                ?.filter((option) => option.is_carton_box)
                .map((option: InventoryOption) => option.id) ?? [],
    );

    const getCartonBoxesCount = computed(() => {
        return model.value.reduce(
            (acc, room) =>
                acc +
                room.items.reduce(
                    (acc, item: MoveInventoryItemData) =>
                        acc +
                        item.qty * (cartonCategoryInventoryOptions.value.includes(item.inventory_option_id) ? 1 : 0),
                    0,
                ),
            0,
        );
    });

    const activeRoomCartonBoxesCount = computed(() => {
        if (activeRoomIndex.value === null) {
            return model.value.reduce(
                (acc, room) =>
                    acc +
                    room.items.reduce(
                        (acc, item: MoveInventoryItemData) =>
                            acc +
                            item.qty *
                                (cartonCategoryInventoryOptions.value.includes(item.inventory_option_id) ? 1 : 0),
                        0,
                    ),
                0,
            );
        }

        return model.value[activeRoomIndex.value].items.reduce(
            (acc, item: MoveInventoryItemData) =>
                acc + item.qty * (cartonCategoryInventoryOptions.value.includes(item.inventory_option_id) ? 1 : 0),
            0,
        );
    });

    function initialise(initialInventory: MoveInventoryGroupData[] | null) {
        // console.log(initialInventory);

        let newModel: MoveInventoryGroupData[] = [];

        inventoryCategories.value?.forEach((category: InventoryCategory) => {
            let existingRooms = initialInventory?.filter(
                (r: MoveInventoryGroupData) => r.inventory_category_id === category.id,
            );
            // console.log(existingRooms);
            if (existingRooms?.length) {
                existingRooms.forEach((r) => {
                    newModel.push({
                        inventory_category_id: category.id,
                        inventory_category_name: category.name,
                        inventory_category_slug: category.slug,
                        room_name: r.room_name,
                        items: r.items.map((item: MoveInventoryItemData) => ({
                            inventory_option_id: item.inventory_option_id,
                            qty: item.qty,
                        })),
                    });
                });
            } else {
                // console.log(category, CUSTOM_CATEGORY_ID.value);
                if (category.id !== CUSTOM_CATEGORY_ID.value && category.id !== CARTON_CATEGORY_ID.value) {
                    newModel.push({
                        inventory_category_id: category.id,
                        inventory_category_name: category.name,
                        inventory_category_slug: category.slug,
                        room_name: category.name,
                        items: [],
                    });
                }
            }
        });

        model.value = newModel;
        isInitialised.value = true;

        // model.value = inventoryCategories.value?.filter((category) => category.id !== CUSTOM_CATEGORY_ID.value).map((category) => ({
        //     inventory_category_id: category.id,
        //     inventory_category_name: category.name,
        //     inventory_category_slug: category.slug,
        //     room_name: category.name,
        //     items: [],
        // }));
    }

    return {
        inventoryCategories,
        CUSTOM_CATEGORY_ID,
        CARTON_CATEGORY_ID,
        model,
        isInitialised,
        activeRoomIndex,
        activeRoomItemCount,
        currentCategory,
        saveLoading, // Export the save loading state
        initialise,
        setActiveRoomIndex,
        getIconByCategorySlug,
        getCartonBoxesCount,
        activeRoomCartonBoxesCount,
        handleDecrease,
        handleIncrease,
        handleSetQuantity,
        addRoom,
        addCustomRoom,
        isFirstRoomOfCategory,
        removeRoom,
        getTotalItems,
        activeRoomIsCustom,
        activeRoomName,
        autoSaveInventory, // Export the auto-save function
        flushAutoSave, // Export the flush auto-save function
    };
});
